import { Suspense, useState, useEffect, lazy, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import classNames from "classnames";
import { Bell, BookOpen, ChevronLeft, ChevronRight, Gift, Lightbulb, Megaphone, Send, ShoppingBasket, Store } from "lucide-react";
import { FaFacebookF, FaGlobe, FaInstagram, FaLinkedinIn, FaTiktok } from "react-icons/fa";
import styles from "../styles/components/userLanding.module.scss";
import teamVisual from "../assets/images/team.svg";
import { loadTenantAsset } from "../redux/tenantSlice";
import { setUTMSource } from "../redux/utmSlice";
import { backendLink, backendLinkComplaint } from "../config";
import LangButtons from "../components/LangButtons";
import { SurveyIcon, TicketIcon } from "./SurveyForm";
import { loadSurvey, surveyMinutes } from "../components/surveyData";
import { useNotificationClick } from "../components/hooks/useNotificationClick";

// Lazy loaded components
const TipsScreen = lazy(() => import("../components/TipsScreen"));
const AlertsSheet = lazy(() => import("../components/AlertsSheet"));
const TermsOfUse = lazy(() => import("../components/TermsOfUse").then((module) => ({ default: module.TermsOfUse })));
const TermsOfUsePopup = lazy(() => import("../components/TermsOfUse").then((module) => ({ default: module.TermsOfUsePopup })));
const ParticipationTermsModal = lazy(() => import("../components/ParticipationTermsModal"));

const SOCIAL_PLATFORMS = [
  { key: "facebook", label: "Facebook", Icon: FaFacebookF },
  { key: "instagram", label: "Instagram", Icon: FaInstagram },
  { key: "tiktok", label: "TikTok", Icon: FaTiktok },
  { key: "website", labelKey: "landingWebsite", Icon: FaGlobe },
  { key: "linkedin", label: "LinkedIn", Icon: FaLinkedinIn },
];

// Forms gated by the terms of use popup
const FORM_ROUTES = {
  form: "/reclamation_q",
  distributor: "/reclamation_d",
  otherRequest: "/contactez-nous",
  suggestion: "/suggest",
  survey: "/sondage",
};

const COMPLAINT_GRADIENT = "var(--complaint-button, linear-gradient(135deg, #e74c3c, #c0392b))";
const GLOBAL_GRADIENT = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";

// ── UI components ──────────────────────────────────────────────────────────────

function Floor({ Icon, disc, alt, label, sub, tag, onClick }) {
  return (
    <button
      type="button"
      className={classNames(styles.floor, { [styles.alt]: alt, [styles.other]: disc === "d3", [styles.survey]: disc === "d5" })}
      onClick={onClick}
    >
      <span className={classNames(styles.disc, styles[disc])}>
        <Icon size={32} aria-hidden />
      </span>
      <span className={styles.floorBody}>
        <span className={styles.lab}>{label}</span>
        <span className={styles.sub}>{sub}</span>
        {tag}
      </span>
      <span className={styles.go}>
        <ChevronRight size={20} strokeWidth={2.4} aria-hidden />
      </span>
    </button>
  );
}

function DuoCard({ Icon, disc, alt, label, sub, onClick }) {
  return (
    <button type="button" className={classNames(styles.duoCard, { [styles.alt]: alt })} onClick={onClick}>
      <span className={classNames(styles.disc, styles[disc])}>
        <Icon size={42} strokeWidth={1.9} aria-hidden />
      </span>
      <span className={styles.lab}>{label}</span>
      <span className={styles.sub}>{sub}</span>
      <span className={styles.go}>
        <ChevronRight size={20} strokeWidth={2.4} aria-hidden />
      </span>
    </button>
  );
}

// Popup shown when the user lands from a push notification
function NotificationDialog({ popup, gradient, textColor, shadow, onClose, onOk }) {
  return (
    <Dialog
      open={!!popup}
      onClose={onClose}
      PaperProps={{ sx: { borderRadius: "16px", minWidth: "280px", maxWidth: "420px", width: "90%", overflow: "hidden" } }}
    >
      {popup?.image && (
        <Box component="img" src={popup.image} alt="" sx={{ width: "100%", maxHeight: "200px", objectFit: "cover", display: "block" }} />
      )}
      <Box sx={{ background: gradient, px: 3, pt: 2.5, pb: 2 }}>
        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: textColor, fontSize: "14px", lineHeight: 1.5 }}>
          {popup?.title}
        </Typography>
      </Box>
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "14px", color: "#444", lineHeight: 1.7 }}>
          {popup?.message}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1 }}>
        <Button
          onClick={onOk}
          fullWidth
          sx={{
            background: gradient,
            color: textColor,
            borderRadius: "25px",
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 600,
            textTransform: "none",
            fontSize: "15px",
            py: 1.2,
            boxShadow: `0 4px 15px ${shadow}`,
            "&:hover": { opacity: 0.9, background: gradient },
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function UserLanding({ currentLang, setCurrentLang }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";

  const bg = useSelector((state) => state.tenant?.tenant.assets?.bg);
  const tenantName = useSelector((state) => state.tenant?.tenant.name) || "base";
  // "bg_new" (e.g. Palmary) is a visual for the new design only; the production pages keep "bg"
  const visual = loadTenantAsset(tenantName, "bg_new") || loadTenantAsset(tenantName, "bg");
  const enabledModules = useSelector((state) => state.tenant?.tenant.enabledModules) || {};
  // Same lookup as components/Header.js; "logo_new" (e.g. Dulcesol) is a logo for the new design only,
  // the production pages keep "logo"
  const logo =
    loadTenantAsset(tenantName, "logo_new") ||
    loadTenantAsset(tenantName, isRtl ? "logo_fr" : "logo_ar") ||
    loadTenantAsset(tenantName, "logo");

  const [screen, setScreen] = useState("home"); // "home" | "choice" | "tips"
  const [formType, setFormType] = useState(null);
  const [isTermsOfUseModalVisible, setIsTermsOfUseModalVisible] = useState(false);
  const [participationTermsModalVisible, setParticipationTermsModalVisible] = useState(false);
  const [isGCUPopupOpened, setGCUPopupOpened] = useState(false);
  const [open, setOpen] = useState(false); // alerts
  const [socialMediaData, setSocialMediaData] = useState({});
  const [socialPopup, setSocialPopup] = useState(null);
  const [notifPopup, setNotifPopup] = useState(null); // { title, message, image, historyId }
  const [complaintPopup, setComplaintPopup] = useState(null); // { title, message, historyId }
  const [survey, setSurvey] = useState(null);

  useEffect(() => {
    loadSurvey()
      .then(setSurvey)
      .catch(() => setSurvey(null));
  }, []);

  // ── Push notifications ──────────────────────────────────────────────────────

  const handleNotification = useCallback((type, data) => {
    switch (type) {
      case "alert":
        setOpen(true);
        break;
      case "complaint":
        setComplaintPopup({
          title: data.title || "",
          message: data.body || "",
          // postMessage path: id lives in data.data.id
          // URL-params path: id lives in data.id
          historyId: data.data?.id || data.id || null,
        });
        break;
      case "auto":
        // No popup — the service worker already opens/focuses /user
        break;
      case "global":
        setNotifPopup({
          title: data.title || "",
          message: data.body || "",
          // postMessage path: image/id live in data.data
          // URL-params path: image/id live directly in data
          image: data.data?.image || data.image || null,
          historyId: data.data?.id || data.id || null,
        });
        break;
      default:
        break;
    }
  }, []);

  useNotificationClick(handleNotification);

  const markSeen = async (endpoint, historyId) => {
    if (!historyId) return;
    try {
      await axios.get(`${backendLinkComplaint}shared/push/${endpoint}/${historyId}`);
    } catch (err) {
      console.error("Error marking notification as seen:", err);
    }
  };

  const handleComplaintOk = () => {
    const historyId = complaintPopup?.historyId;
    setComplaintPopup(null);
    markSeen("seenComplaintNotification", historyId);
  };

  const handleGlobalOk = () => {
    const historyId = notifPopup?.historyId;
    setNotifPopup(null);
    markSeen("seenGlobalNotification", historyId);
  };

  // ── Mount effects ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (enabledModules.termsOfUse) {
      localStorage.removeItem("conditionsAccepted");
      localStorage.removeItem("participationConditionsAccepted");
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${backendLink}shared/getSocialMedia`)
      .then((response) => {
        const items = response.data?.data?.social_media || [];
        const grouped = {};
        items.forEach((item) => {
          const platform = item.platform?.toLowerCase();
          if (!platform) return;
          if (!grouped[platform]) grouped[platform] = [];
          grouped[platform].push(item);
        });
        setSocialMediaData(grouped);
      })
      .catch((err) => {
        console.error("Error fetching social media:", err);
      });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlUTM = params.get("utm");

    if (urlUTM) {
      dispatch(setUTMSource(urlUTM));
      sessionStorage.setItem("utm_source", urlUTM);
    } else {
      const storedUTM = sessionStorage.getItem("utm_source");
      if (storedUTM) dispatch(setUTMSource(storedUTM));
    }
  }, []);

  // ── Navigation ──────────────────────────────────────────────────────────────

  const goToForm = (type) => {
    const conditionsAccepted = localStorage.getItem("conditionsAccepted");
    setFormType(type);
    if (!conditionsAccepted && enabledModules.termsOfUse) {
      setIsTermsOfUseModalVisible(true);
    } else {
      navigate(FORM_ROUTES[type]);
    }
  };

  const onAccept = () => {
    localStorage.setItem("conditionsAccepted", "true");
    setIsTermsOfUseModalVisible(false);
    if (FORM_ROUTES[formType]) navigate(FORM_ROUTES[formType]);
  };

  const goToConcoursForm = () => {
    const participationConditionsAccepted = localStorage.getItem("participationConditionsAccepted");
    if (!participationConditionsAccepted && enabledModules.termsOfUse) {
      setParticipationTermsModalVisible(true);
    } else {
      navigate("/concours");
    }
  };

  const onAcceptParticipation = () => {
    localStorage.setItem("participationConditionsAccepted", "true");
    setParticipationTermsModalVisible(false);
    navigate("/concours");
  };

  // With both modules the complaint floor opens the consumer / merchant choice
  const onComplaint = () => {
    if (enabledModules.merchantModule && enabledModules.consumerModule) {
      setScreen("choice");
    } else {
      goToForm(enabledModules.merchantModule ? "distributor" : "form");
    }
  };

  // ── Screens ─────────────────────────────────────────────────────────────────

  const home = (
    <section className={classNames(styles.screen, styles.home)}>
      {/* same places in every language: the bar does not mirror in Arabic */}
      <div className={styles.homeTop} dir="ltr">
        <LangButtons currentLang={currentLang} setCurrentLang={setCurrentLang} />
        <div className={styles.homeIcons}>
          {enabledModules.tipsRecipesModule && (
            <button
              type="button"
              className={styles.iconRound}
              aria-label={t(`buttonForYou_${tenantName}`)}
              title={t(`buttonForYou_${tenantName}`)}
              onClick={() => setScreen("tips")}
            >
              <BookOpen size={22} strokeWidth={1.8} aria-hidden />
            </button>
          )}
          <button
            type="button"
            className={styles.iconRound}
            aria-label={t("landingAlerts")}
            title={t("landingAlerts")}
            onClick={() => setOpen(true)}
          >
            <Bell size={22} strokeWidth={1.8} aria-hidden />
          </button>
        </div>
      </div>

      {logo && (
        <div className={styles.logoWrap}>
          <img className={styles.logo} src={logo} alt="Logo" loading="eager" decoding="async" />
        </div>
      )}
      <h1>{t("landingWelcome")}</h1>
      <p className={styles.ask}>{t("landingAsk")}</p>

      {(enabledModules.merchantModule || enabledModules.consumerModule) && (
        <Floor Icon={Megaphone} disc="d1" label={t("rcComplaint")} sub={t("landingComplaintSub")} onClick={onComplaint} />
      )}
      {/* like the other modules: shown when the tenant config enables it */}
      {enabledModules.suggestion && (
        <Floor
          Icon={Lightbulb}
          disc="d2"
          alt
          label={t("sgSuggestion")}
          sub={t("landingSuggestionSub")}
          onClick={() => goToForm("suggestion")}
        />
      )}
      {enabledModules.OtherRequest && (
        <Floor Icon={Send} disc="d3" label={t("odTitle")} sub={t("landingOtherSub")} onClick={() => goToForm("otherRequest")} />
      )}
      {/* Sondage: shown while a survey is in progress */}
      {survey && (
        <Floor
          Icon={SurveyIcon}
          disc="d5"
          label={t("svTitle")}
          sub={t("svFloorSub", { n: survey.questions.length, m: surveyMinutes(survey.questions.length) })}
          tag={
            survey.tombola && (
              <span className={styles.tag}>
                <TicketIcon size={15} strokeWidth={2} />
                <span>{t("svTag")}</span>
              </span>
            )
          }
          onClick={() => goToForm("survey")}
        />
      )}
      {enabledModules.lottery && (
        <Floor Icon={Gift} disc="d4" label={t("buttonConcours")} sub={t("landingConcoursSub")} onClick={goToConcoursForm} />
      )}

      <div className={styles.homeFoot}>
        {/* JawdaPlus: its four characters. A brand: its own visual (its products). */}
        {tenantName === "base" ? (
          <div className={classNames(styles.visual, styles.team)}>
            <img src={teamVisual} alt="" loading="eager" decoding="async" />
          </div>
        ) : (
          (visual || bg) && (
            <div className={styles.visual}>
              <img src={visual} alt="" loading="eager" decoding="async" />
            </div>
          )
        )}
        <div className={styles.footLinks}>
          {SOCIAL_PLATFORMS.map(({ key, label, labelKey, Icon }) => {
            const items = socialMediaData[key];
            if (!items || items.length === 0) return null;
            const name = label || t(labelKey);
            const className = classNames(styles.soc, styles[key]);
            if (items.length === 1) {
              return (
                <a key={key} className={className} href={items[0].url} target="_blank" rel="noopener noreferrer" aria-label={name} title={name}>
                  <Icon aria-hidden />
                </a>
              );
            }
            return (
              <button type="button" key={key} className={className} aria-label={name} title={name} onClick={() => setSocialPopup(key)}>
                <Icon aria-hidden />
              </button>
            );
          })}
          {enabledModules.termsOfUse && (
            <button type="button" className={styles.cgu} onClick={() => setGCUPopupOpened(true)}>
              {t("landingCgu")}
            </button>
          )}
        </div>
        <a className={styles.powered} href="https://kozalia.com/jawdaplus/" target="_blank" rel="noopener noreferrer">
          Powered by JawdaPlus. Copyright © 2026 Eurl KOZALIA. All rights reserved.
        </a>
      </div>
    </section>
  );

  const choice = (
    <section className={styles.screen}>
      {/* same places in every language: back on the left, title next to it, language on the right */}
      <div className={styles.cardHeader} dir="ltr">
        <button
          type="button"
          className={styles.headerBack}
          aria-label={t("rcBack")}
          title={t("rcBack")}
          onClick={() => setScreen("home")}
        >
          <ChevronLeft size={24} strokeWidth={2.4} aria-hidden />
        </button>
        <h1 className={styles.headerTitle} dir={isRtl ? "rtl" : "ltr"}>
          {t("rcComplaint")}
        </h1>
        <LangButtons align="end" currentLang={currentLang} setCurrentLang={setCurrentLang} />
      </div>
      <div className={styles.choiceBody}>
        <p className={styles.pageQ}>{t("landingChoiceQ")}</p>
        <div className={styles.duo}>
          <DuoCard
            Icon={ShoppingBasket}
            disc="d1"
            label={t("landingConsumer")}
            sub={t("landingConsumerHint")}
            onClick={() => goToForm("form")}
          />
          <DuoCard
            Icon={Store}
            disc="d2"
            alt
            label={t("landingMerchant")}
            sub={t("landingMerchantHint")}
            onClick={() => goToForm("distributor")}
          />
        </div>
      </div>
    </section>
  );

  return (
    <div className={styles.page} data-tenant={tenantName}>
      {/* same layout in every language (client request): only the texts change */}
      <div className={styles.card} dir="ltr">
        {screen === "home" && home}
        {screen === "choice" && choice}
        {screen === "tips" && (
          <section className={styles.screen}>
            <Suspense fallback={null}>
              <TipsScreen
                title={t(`buttonForYou_${tenantName}`)}
                onBack={() => setScreen("home")}
                currentLang={currentLang}
                setCurrentLang={setCurrentLang}
              />
            </Suspense>
          </section>
        )}
      </div>

      <Dialog
        open={!!socialPopup}
        onClose={() => setSocialPopup(null)}
        PaperProps={{ sx: { borderRadius: "16px", minWidth: "280px", maxWidth: "380px", width: "90%" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, textTransform: "capitalize" }}>{socialPopup}</Typography>
          <IconButton onClick={() => setSocialPopup(null)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {(socialMediaData[socialPopup] || []).map((item, idx) => (
            <a key={idx} href={item.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  py: 1.5,
                  px: 1,
                  borderRadius: "10px",
                  "&:hover": { backgroundColor: "#f5f5f5" },
                  cursor: "pointer",
                }}
              >
                {item.icon ? (
                  <img
                    src={item.icon}
                    alt={item.brand?.name || ""}
                    style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                  />
                ) : (
                  <Box sx={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: "#e0e0e0", flexShrink: 0 }} />
                )}
                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "14px", color: "#333", fontWeight: 500 }}>
                  {item.brand?.name || item.url}
                </Typography>
              </Box>
            </a>
          ))}
        </DialogContent>
      </Dialog>

      <NotificationDialog
        popup={complaintPopup}
        gradient={COMPLAINT_GRADIENT}
        textColor="var(--complaintTextColor, white)"
        shadow="rgba(231, 76, 60, 0.3)"
        onClose={() => setComplaintPopup(null)}
        onOk={handleComplaintOk}
      />
      <NotificationDialog
        popup={notifPopup}
        gradient={GLOBAL_GRADIENT}
        textColor="white"
        shadow="rgba(102, 126, 234, 0.3)"
        onClose={() => setNotifPopup(null)}
        onOk={handleGlobalOk}
      />

      <Suspense fallback={null}>
        {open && <AlertsSheet onClose={() => setOpen(false)} />}
        <TermsOfUse isVisible={isTermsOfUseModalVisible} onAccept={onAccept} onRefuse={() => setIsTermsOfUseModalVisible(false)} />
        <TermsOfUsePopup isOpen={isGCUPopupOpened} setOpen={setGCUPopupOpened} />
        <ParticipationTermsModal
          isOpen={participationTermsModalVisible}
          onAccept={onAcceptParticipation}
          onRefuse={() => setParticipationTermsModalVisible(false)}
        />
      </Suspense>
    </div>
  );
}
